import React, { useContext, useState } from 'react';

import dayjs from 'dayjs';

import getPriceTier from 'common/billing/getPriceTier';
import { CompanyContext } from 'common/containers/CompanyContainer';
import Timestamp from 'common/Timestamp';
import Card from 'common/uiV3/Card';
import getPlanNameForTrial from 'common/util/getPlanNameForTrial';
import isFree from 'common/util/isFree';
import isNil from 'common/util/isNil';
import numberWithCommas from 'common/util/numberWithCommas';
import PlanTierLimitLineItem from './PlanTierLimitLineItem';
import { getPlanName } from './utils';

import type { Discount, FullBillingData } from 'common/api/endpoints/companies';
import Button from 'common/uiV3/Button';
import Flex from 'common/uiV3/Flex';
import HStack from 'common/uiV3/HStack';
import Tooltip from 'common/uiV3/Tooltip';
import VStack from 'common/uiV3/VStack';
import { Edit, Trash } from 'lucide-react';

export const LineItem = ({
  label,
  value,
  actions,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  actions?: React.ReactNode[];
}) => {
  return (
    <VStack className="gap-1.5">
      <div className="text-secondary-foreground">{label}</div>
      <Flex className="gap-2 items-center max-h-4.5">
        {value ? <>{value}</> : null}
        {actions?.length ? <HStack className="gap-1">{actions}</HStack> : null}
      </Flex>
    </VStack>
  );
};

const discountString = (discount: Discount) => {
  const amount =
    (discount.amountOff ? '$' : '') +
    (discount.amountOff ? discount.amountOff / 100 : discount.percentOff) +
    (discount.percentOff ? '%' : '') +
    ' off';

  let duration;
  if (discount.duration === 'forever') {
    duration = 'forever';
  } else if (discount.duration === 'once') {
    duration = 'once';
  } else {
    duration = 'for ' + discount.durationInMonths + ' months';
  }

  return `${amount} ${duration}`;
};

export const TrialCard = ({
  onEndTrial,
}: {
  onEndTrial: (options: { confirm: boolean }) => Promise<void>;
}) => {
  const { planTrial, billingData } = useContext(CompanyContext);
  const [isEndingTrial, setIsEndingTrial] = useState(false);

  const handleEndTrial = async () => {
    if (isEndingTrial) {
      return;
    }

    setIsEndingTrial(true);
    await onEndTrial({ confirm: false });
    setIsEndingTrial(false);
  };

  if (!billingData?.plan || !planTrial || planTrial.cancelled || planTrial.upgraded) {
    return null;
  }

  const planName = getPlanNameForTrial(planTrial);

  return (
    <BaseBillingCard title="Plan trial">
      <LineItem label="Plan name" value={planName} />
      <LineItem
        label="Trial ends"
        value={<Timestamp showRelative={false} timestamp={planTrial.end} />}
        actions={[
          <Button
            key="end-trial"
            size="xsmall"
            variant="link"
            onClick={handleEndTrial}
            isLoading={isEndingTrial}>
            End trial now
          </Button>,
        ]}
      />
    </BaseBillingCard>
  );
};

export const PlanCard = () => {
  const { billingData, contributorCount, discount, monthlySpend } = useContext(CompanyContext);

  if (!billingData) {
    return null;
  }

  const { plan } = billingData;
  return (
    <BaseBillingCard title="Your plan">
      <LineItem label="Plan name" value={getPlanName(plan?.planID) ?? 'None'} />
      {plan ? (
        <>
          <LineItem
            label="Tracked users"
            value={`${numberWithCommas(contributorCount)}${
              plan.tiered
                ? ` / ${numberWithCommas(getPriceTier(plan, contributorCount).users)}`
                : ''
            }`}
          />
          <LineItem
            label="Price"
            value={`$${numberWithCommas(Math.ceil(monthlySpend / 100))}/mo`}
          />
          <PlanTierLimitLineItem
            billingData={billingData as FullBillingData}
            contributorCount={contributorCount}
          />
          {discount ? <LineItem label="Discount" value={discountString(discount)} /> : null}
        </>
      ) : null}
    </BaseBillingCard>
  );
};

const BillingStatusLineItem = ({
  billingData,
  onQualifyCurrentPlan,
}: {
  billingData: FullBillingData;
  onQualifyCurrentPlan: () => void;
}) => {
  const { plan } = billingData;

  let status = 'Active';
  let showUnexpireAction = false;
  if (!plan) {
    status = 'Cancelled';
  } else if (billingData.expired) {
    status = 'Expired';
    showUnexpireAction = true;
  } else if (billingData.scheduledChange) {
    const planName = getPlanName(billingData.scheduledPlanID);
    status = planName ? `Moving to "${planName}"` : 'Changing plan';
  } else if (
    billingData.cancelled &&
    billingData.activeUntil &&
    billingData.activeUntil > dayjs().toDate()
  ) {
    status = 'Cancelling';
  }

  return (
    <LineItem
      label="Status"
      value={
        <>
          {status}
          {billingData.activeUntil && status !== 'Active' ? (
            <>
              {' '}
              on <Timestamp showRelative={false} timestamp={billingData.activeUntil} />
            </>
          ) : null}
        </>
      }
      actions={
        showUnexpireAction
          ? [
              <Button key="unexpire" size="xsmall" onClick={onQualifyCurrentPlan}>
                Unexpire
              </Button>,
            ]
          : undefined
      }
    />
  );
};

export const BillingCard = ({
  onChangeInvoiceEmail,
  onDeleteCard,
  onQualifyCurrentPlan,
  onShowInvoices,
  onUpdateCard,
}: {
  onChangeInvoiceEmail: () => void;
  onDeleteCard: () => Promise<void>;
  onQualifyCurrentPlan: () => void;
  onShowInvoices: () => void;
  onUpdateCard: () => void;
}) => {
  const { billingData } = useContext(CompanyContext);
  const [isDeletingCard, setIsDeletingCard] = useState(false);

  if (!billingData || !('cancelled' in billingData)) {
    return null;
  }

  const inactive =
    billingData.cancelled ||
    isNil(billingData.activeUntil) ||
    billingData.activeUntil < dayjs().toDate();

  const isOnFreePlan = billingData.plan && isFree(billingData.plan.planID);
  const isExpired = billingData.expired;
  const canDeleteCard = billingData.hasCard && (isOnFreePlan || isExpired);

  const handleDeleteCard = async () => {
    if (isDeletingCard) {
      return;
    }
    setIsDeletingCard(true);
    await onDeleteCard();
    setIsDeletingCard(false);
  };

  const getCardActions = () => {
    const actions = [];

    if (billingData.hasCard) {
      actions.push(
        <Tooltip key="change">
          <Tooltip.Trigger asChild>
            <Button size="xsmall" iconOnly onClick={onUpdateCard}>
              <Edit />
            </Button>
          </Tooltip.Trigger>
          <Tooltip.Content>Change card or address</Tooltip.Content>
        </Tooltip>,
      );
    } else {
      actions.push(
        <Button key="add" size="xsmall" variant="link" onClick={onUpdateCard}>
          Add card
        </Button>,
      );
    }

    if (canDeleteCard) {
      actions.push(
        <Tooltip key="delete">
          <Tooltip.Trigger asChild>
            <Button
              size="xsmall"
              iconOnly
              color="error"
              onClick={handleDeleteCard}
              isLoading={isDeletingCard}>
              <Trash />
            </Button>
          </Tooltip.Trigger>
          <Tooltip.Content>Delete card</Tooltip.Content>
        </Tooltip>,
      );
    }

    return actions;
  };

  return (
    <BaseBillingCard title="Billing">
      <LineItem
        label="Next billing cycle"
        value={
          billingData.activeUntil && !inactive ? (
            <Timestamp showRelative={false} timestamp={billingData.activeUntil} />
          ) : (
            'Never'
          )
        }
      />
      <BillingStatusLineItem
        billingData={billingData}
        onQualifyCurrentPlan={onQualifyCurrentPlan}
      />
      {billingData.plan && !isFree(billingData.plan.planID) ? (
        <LineItem
          label="Billing email"
          value={billingData.invoiceEmail}
          actions={[
            <Button key="change" size="xsmall" onClick={onChangeInvoiceEmail}>
              Change
            </Button>,
          ]}
        />
      ) : null}
      <LineItem
        label="Credit card"
        value={billingData.hasCard ? `Ends with ${billingData.last4}` : null}
        actions={getCardActions()}
      />
      <LineItem
        label="History"
        value={null}
        actions={[
          <Button key="invoices" variant="link" onClick={onShowInvoices}>
            See invoices
          </Button>,
        ]}
      />
    </BaseBillingCard>
  );
};

const BaseBillingCard = ({ title, children }: { title: string; children: React.ReactNode }) => {
  return (
    <Card className="w-[330px]" size="large">
      <h2 className="text-2xl font-medium mb-3.5">{title}</h2>
      <VStack className="gap-4">{children}</VStack>
    </Card>
  );
};
